import type { AuthSession } from './auth';

/** Fetch wrapper: bearer token when one exists, typed JSON out, 401 -> reload into login. */
export class ApiClient {
  constructor(private readonly session: AuthSession) {}

  async get<T>(path: string): Promise<T> {
    return this.request<T>(path, {});
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /** Binary POST (a logo file): the bytes go up raw and the server reads
   *  the type from them, so a browser's guess at a content-type never
   *  decides what gets stored. */
  async postBinary<T>(path: string, file: Blob): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      headers: { 'content-type': 'application/octet-stream' },
      body: file,
    });
  }

  /** JSON in, audio out (A.F.R.O.B.O.T's voice). Same reason as getBlob:
   *  the bearer has to travel, so the browser cannot just point an
   *  <audio src> at the route. */
  async postForBlob(path: string, body: unknown): Promise<Blob> {
    const token = this.session.getAccessToken();
    const res = await fetch(path, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`${path} failed: ${res.status}`);
    this.lastVoice = res.headers.get('x-afrobot-voice');
    return res.blob();
  }

  /** Which voice the last /api/speak answer used: 'licensed' or 'placeholder'. */
  lastVoice: string | null = null;

  /** Binary GET (avatars): the bearer must travel, so a bare <img src>
   *  cannot do this; callers turn the blob into an object URL. */
  async getBlob(path: string): Promise<Blob> {
    const token = this.session.getAccessToken();
    const res = await fetch(path, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 401) {
      window.location.reload();
      throw new Error('unauthenticated');
    }
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return res.blob();
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const token = this.session.getAccessToken();
    const res = await fetch(path, {
      ...init,
      headers: {
        ...(init.headers as Record<string, string> | undefined),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (res.status === 401) {
      window.location.reload(); // startAuth will bounce through Cognito
      throw new Error('unauthenticated');
    }
    if (!res.ok) {
      throw new ApiError(res.status, await res.text());
    }
    return (await res.json()) as T;
  }
}

export class ApiError extends Error {
  readonly code: string | null;

  constructor(
    readonly status: number,
    body: string,
  ) {
    super(`API ${status}: ${body.slice(0, 200)}`);
    let code: string | null = null;
    try {
      code = (JSON.parse(body) as { error?: string }).error ?? null;
    } catch {
      /* not json */
    }
    this.code = code;
  }
}
